// @ts-check
// src/home-logic.js
// Pure functions used by the home pane. Loaded as ESM:
//   - tests/home-logic.test.js imports directly
//   - index.html attaches the exports to window.HomeLogic via a small shim
// No DOM or state mutation here; everything takes (state, today) and returns data.

/**
 * Compute the budget-headroom signal for the headline ring on the home pane.
 *
 * @param {object} state  the app state (uses state.budgets, state.dailyLog,
 *                        state.expenses, state.settings.monthlyBudget).
 * @param {Date}   today  current date (injectable for testing).
 * @returns {{
 *   state: "ahead"|"onpace"|"behind"|"over"|"empty",
 *   budget: number, spent: number,
 *   pctLeft: number,
 *   paceDelta: number,
 *   dayOfMonth: number, daysInMonth: number,
 *   unbudgetedCategories: string[], unbudgetedSpend: number
 * }}
 */
export function computeBudgetHeadroom(state, today){
  // Guard: invalid/missing today → fall back to now (per Task 1 quality review).
  if (!(today instanceof Date) || isNaN(today.valueOf())) today = new Date();
  const budget = sumBudgets(state) ?? 0;
  const monthPrefix = monthKey(today);
  const dayOfMonth = today.getUTCDate();
  const daysInMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth()+1, 0)).getUTCDate();

  // Spent = (a) dailyLog entries this month + (b) recurring bills marked paid this month
  let spent = 0;
  const categorySpend = Object.create(null);
  for (const l of state.dailyLog || []){
    if (typeof l?.date !== "string" || !l.date.startsWith(monthPrefix + "-")) continue;
    const amt = Math.max(0, Number(l.amount) || 0);
    spent += amt;
    const cat = l.cat || "Other";
    categorySpend[cat] = (categorySpend[cat] || 0) + amt;
  }
  for (const e of state.expenses || []){
    if (!Array.isArray(e?.paidMonths)) continue;
    if (!e.paidMonths.includes(monthPrefix)) continue;
    const amt = Math.max(0, Number(e.amount) || 0);
    spent += amt;
    const cat = e.cat || "Bills";
    categorySpend[cat] = (categorySpend[cat] || 0) + amt;
  }

  // Empty: no budget configured at all
  if (budget <= 0){
    return {
      state: "empty", budget: 0, spent,
      pctLeft: 0, paceDelta: 0,
      dayOfMonth, daysInMonth, unbudgetedCategories: [], unbudgetedSpend: 0,
    };
  }

  const expected = budget * (dayOfMonth / daysInMonth);
  const paceDelta = spent - expected;
  const tolerance = 0.05 * budget;
  let st;
  if (spent > budget) st = "over";
  else if (paceDelta > tolerance) st = "behind";
  else if (paceDelta < -tolerance) st = "ahead";
  else st = "onpace";

  // Unbudgeted spend: categories that have spend but no entry in state.budgets
  // A category is "budgeted" if it's listed at all — including budget=0 (user
  // consciously added it). Numeric NaN or non-numeric values are ignored.
  const budgetedCats = new Set(
    Object.keys(state.budgets || {}).filter(k => Number.isFinite(Number(state.budgets[k])))
  );
  const unbudgetedCategories = [];
  let unbudgetedSpend = 0;
  for (const cat of Object.keys(categorySpend)){
    if (!budgetedCats.has(cat)){
      unbudgetedCategories.push(cat);
      unbudgetedSpend += categorySpend[cat];
    }
  }

  return {
    state: /** @type {"empty"|"ahead"|"onpace"|"behind"|"over"} */ (st),
    budget, spent,
    pctLeft: Math.min(1, Math.max(0, (budget - spent) / budget)),
    paceDelta,
    dayOfMonth, daysInMonth,
    unbudgetedCategories, unbudgetedSpend,
  };
}

function sumBudgets(state){
  const b = state?.budgets;
  if (!b || typeof b !== "object" || Array.isArray(b)) return state?.settings?.monthlyBudget ?? 0;
  let total = 0;
  for (const k of Object.keys(b)){
    const v = Number(b[k]);
    if (Number.isFinite(v) && v > 0) total += v;
  }
  if (total > 0) return total;
  return state?.settings?.monthlyBudget ?? 0;
}

// Local helper — also used by getTopAnomaly later (Task 4).
function monthKey(d){
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth()+1).padStart(2, "0");
  return `${y}-${m}`;
}

/**
 * Build the unified "today & due" worklist for the home pane.
 * Merges bills, tasks, subscriptions that are overdue, due today, or due within 3 days.
 *
 * @param {object} state            uses state.expenses, state.tasks, state.subscriptions
 * @param {Date}   today
 * @param {object} [opts]           { cap=5, windowDays=3 }
 * @returns {Array<{ kind:'bill'|'task'|'subscription', bucket:'overdue'|'today'|'soon',
 *                   title:string, amount?:number, dueDate:string, daysUntil:number, ref:any }>}
 */
export function getWorklistItems(state, today, opts){
  if (!(today instanceof Date) || isNaN(today.valueOf())) today = new Date();
  const cap = opts?.cap ?? 5;
  const windowDays = opts?.windowDays ?? 3;
  const todayMid = startOfDayUTC(today);

  const items = [];
  for (const e of state.expenses || []){
    if (e?.paid) continue;
    if (!e?.dueDate) continue;
    const d = daysBetween(todayMid, parseDate(e.dueDate));
    if (isNaN(d) || d > windowDays) continue;
    items.push({
      kind: "bill", title: e.name || "(untitled bill)",
      amount: Number(e.amount) || 0, dueDate: e.dueDate,
      daysUntil: d, bucket: bucketOf(d), ref: e,
    });
  }
  for (const t of state.tasks || []){
    if (t?.done) continue;
    if (!t?.dueDate) continue;
    const d = daysBetween(todayMid, parseDate(t.dueDate));
    if (isNaN(d) || d > windowDays) continue;
    items.push({
      kind: "task", title: t.title || "(untitled task)",
      dueDate: t.dueDate, daysUntil: d, bucket: bucketOf(d), ref: t,
    });
  }
  for (const s of state.subscriptions || []){
    if (!s?.nextRenewal) continue;
    const d = daysBetween(todayMid, parseDate(s.nextRenewal));
    if (isNaN(d) || d > windowDays) continue;
    items.push({
      kind: "subscription", title: s.name || "(untitled subscription)",
      amount: Number(s.amount) || 0, dueDate: s.nextRenewal,
      daysUntil: d, bucket: bucketOf(d), ref: s,
    });
  }

  const bucketRank = { overdue: 0, today: 1, soon: 2 };
  const kindRank = { bill: 0, task: 1, subscription: 2 };
  items.sort((a, b) => {
    if (a.bucket !== b.bucket) return bucketRank[a.bucket] - bucketRank[b.bucket];
    if (a.bucket === "overdue"){
      if (a.daysUntil !== b.daysUntil) return a.daysUntil - b.daysUntil; // most overdue first
      // fall through to kind tiebreaker below
    }
    if (kindRank[a.kind] !== kindRank[b.kind]) return kindRank[a.kind] - kindRank[b.kind];
    if (a.kind === "bill") return (b.amount || 0) - (a.amount || 0);   // bills: amount desc
    return (a.dueDate || "").localeCompare(b.dueDate || "");           // tasks/subs: date asc
  });

  return /** @type {any} */ (items.slice(0, cap));
}

function bucketOf(daysUntil){
  if (daysUntil < 0) return "overdue";
  if (daysUntil === 0) return "today";
  return "soon";
}

function startOfDayUTC(d){
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function parseDate(s){
  // Accepts "YYYY-MM-DD"; returns Invalid Date if any component is missing.
  const parts = String(s).slice(0, 10).split("-").map(Number);
  const [y, m, dd] = parts;
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(dd)) return new Date(NaN);
  if (y < 1900 || m < 1 || m > 12 || dd < 1 || dd > 31) return new Date(NaN);
  return new Date(Date.UTC(y, m - 1, dd));
}

function daysBetween(a, b){
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

/**
 * Return the most significant spending anomaly for the current month, or null.
 * Heuristic: for each category with >= 3 baseline transactions across the prior
 * 3 months, compare this month's pace to that baseline. Return the top result
 * whose absolute deviation >= 15%.
 *
 * Note on baselineAvg: this is the AVERAGE OVER MONTHS THAT HAD DATA for the
 * category (not the 3-month average). A category that was active only in April
 * uses april_total as its baseline, not april_total/3. This favors sensitivity
 * (detects anomalies in sporadic categories) over conservatism (might fire on
 * single-month surges). Intentional.
 *
 * @param {object} state  uses state.dailyLog
 * @param {Date}   today
 * @returns {null | { category:string, direction:"over"|"under",
 *                    deltaPct:number, pace:number, baselineAvg:number,
 *                    currentSpend:number }}
 */
export function getTopAnomaly(state, today){
  if (!(today instanceof Date) || isNaN(today.valueOf())) today = new Date();
  const log = state?.dailyLog;
  if (!Array.isArray(log) || log.length === 0) return null;

  const currentMonth = monthKey(today);
  const baselineMonths = priorMonths(today, 3);
  const baselineSet = new Set(baselineMonths);
  const dayOfMonth = today.getUTCDate();
  const daysInMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth()+1, 0)).getUTCDate();

  // Day-1/2 guard: pace = current * (daysInMonth / dayOfMonth), so a single
  // 1,000-baht expense on day 1 of a 31-day month projects to 31,000 — almost
  // guaranteed false positive. Skip the function entirely for the first 2 days
  // of the month. Genuine early-month anomalies are too rare to be worth false
  // positives at every month-start.
  if (dayOfMonth < 3) return null;

  // Aggregate by category × month
  const byCat = Object.create(null);
  for (const l of log){
    if (typeof l?.date !== "string") continue;
    const mk = l.date.slice(0, 7);
    if (mk !== currentMonth && !baselineSet.has(mk)) continue;
    const cat = l.cat || "Other";
    if (!byCat[cat]) byCat[cat] = { current: 0, baseline: {}, baselineCount: 0 };
    const amt = Math.max(0, Number(l.amount) || 0);
    if (mk === currentMonth){
      byCat[cat].current += amt;
    } else {
      byCat[cat].baseline[mk] = (byCat[cat].baseline[mk] || 0) + amt;
      byCat[cat].baselineCount += 1;
    }
  }

  let best = /** @type {any} */ (null);
  for (const cat of Object.keys(byCat)){
    const c = byCat[cat];
    if (c.baselineCount < 3) continue;
    const months = baselineMonths.filter(m => c.baseline[m] != null);
    if (!months.length) continue;
    const baselineAvg = months.reduce((s, m) => s + (c.baseline[m] || 0), 0) / months.length;
    if (baselineAvg <= 0) continue;
    const pace = c.current / (dayOfMonth / daysInMonth);
    const deltaPct = (pace - baselineAvg) / baselineAvg;
    if (Math.abs(deltaPct) < 0.15) continue;
    const score = Math.abs(deltaPct) * Math.log(1 + c.current);
    if (!best || score > best.score){
      best = {
        score,
        category: cat,
        direction: deltaPct > 0 ? "over" : "under",
        deltaPct,
        pace, baselineAvg,
        currentSpend: c.current,
      };
    }
  }
  if (!best) return null;
  // strip internal-only score
  delete best.score;
  return best;
}

function priorMonths(today, n){
  const out = [];
  for (let i = 1; i <= n; i++){
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - i, 1));
    out.push(monthKey(d));
  }
  return out;
}
